import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Video, Clapperboard, Loader2, Download, Circle } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fileUrl } from "../site/HeroSlides";

const pickMime = () => ["video/mp4;codecs=avc1", "video/mp4", "video/webm;codecs=vp9", "video/webm"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const click = (sel) => document.querySelector(`[data-testid="invite-phone-preview"] [data-testid="${sel}"]`)?.click();

// Records the live phone preview (Chrome / Edge on a computer) using tab capture cropped to the invite.
function RecordExact({ inv, names }) {
  const [state, setState] = useState("");
  const ok = !!navigator.mediaDevices?.getDisplayMedia && !!window.MediaRecorder && !/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
  async function record() {
    let stream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: { displaySurface: "browser", frameRate: 30 }, audio: true, preferCurrentTab: true, selfBrowserSurface: "include" });
    } catch { return toast.error("Choose 'This tab' to record your invite"); }
    const [track] = stream.getVideoTracks();
    const el = document.querySelector('[data-testid="invite-phone-preview"]');
    el.scrollIntoView({ block: "center" });
    if (window.CropTarget && track.cropTo) { try { await track.cropTo(await window.CropTarget.fromElement(el)); } catch { /* full tab fallback */ } }
    const mime = pickMime();
    const mr = new MediaRecorder(stream, mime ? { mimeType: mime, videoBitsPerSecond: 6_000_000 } : undefined);
    const chunks = [];
    mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    mr.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      const type = (mr.mimeType || "video/webm").split(";")[0];
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob(chunks, { type }));
      a.download = `${names.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-invite.${type.includes("mp4") ? "mp4" : "webm"}`;
      a.click(); setState(""); toast.success("Your invite video is downloaded, ready for Instagram & TikTok");
    };
    click("invite-replay"); await sleep(900);
    mr.start(500); setState("rec");
    await sleep(1500); click("invite-open"); click("invite-open-skip");
    const scenes = (inv.scenes || []).filter((s) => s.type !== "rsvp").length;
    await sleep(Math.min(60000, 9000 + scenes * 7000));
    if (mr.state === "recording") mr.stop();
  }
  return (
    <div className="space-y-1.5" data-testid="video-record">
      <p className="text-xs font-medium flex items-center gap-1.5"><Video className="w-3.5 h-3.5 text-[var(--gold)]" /> Record it exactly as guests see it</p>
      {ok ? <button onClick={record} disabled={!!state} className="btn-outline-gold text-xs" data-testid="video-record-btn">{state ? <><Circle className="w-3 h-3 fill-red-500 text-red-500 animate-pulse" /> Recording…</> : "Record vertical video"}</button>
        : <p className="text-[11px] text-[var(--muted)]" data-testid="video-record-unsupported">Open this page on a computer in Chrome or Edge to record, or make an AI cinematic clip below.</p>}
      <p className="text-[10px] text-[var(--muted)]">When asked, pick "This tab". The invite plays from the start and downloads when it ends.</p>
    </div>
  );
}

function AiClip({ inv }) {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ source: inv.art_path ? "art" : "photo", media_id: "", style: "", duration: "5" });
  const [busy, setBusy] = useState(false);
  const photos = (inv.media || []).filter((m) => m.type === "image");
  const load = useCallback(() => api.get(`/weddings/mine/invites/${inv.invite_id}/videos`).then((r) => setList(r.data)).catch(() => {}), [inv.invite_id]);
  useEffect(() => { load(); }, [load]);
  const running = list.some((v) => v.status === "running");
  useEffect(() => { if (!running) return undefined; const t = setInterval(load, 8000); return () => clearInterval(t); }, [running, load]);
  async function make() {
    setBusy(true);
    try { await api.post(`/weddings/mine/invites/${inv.invite_id}/video`, { ...f, media_id: f.source === "photo" ? f.media_id || photos[0]?.media_id || "" : "" }); toast.success("Filming your clip. This takes a few minutes."); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  return (
    <div className="space-y-2" data-testid="video-ai">
      <p className="text-xs font-medium flex items-center gap-1.5"><Clapperboard className="w-3.5 h-3.5 text-[var(--gold)]" /> AI cinematic clip</p>
      <div className="flex gap-2">
        <select value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} className="input-luxe !py-1 text-xs" data-testid="video-ai-source">
          <option value="art" disabled={!inv.art_path}>From AI artwork{inv.art_path ? "" : " (paint it first)"}</option>
          <option value="photo" disabled={!photos.length}>From a photo{photos.length ? "" : " (add photos first)"}</option>
        </select>
        <select value={f.duration} onChange={(e) => setF({ ...f, duration: e.target.value })} className="input-luxe !py-1 text-xs !w-24" data-testid="video-ai-duration"><option value="5">5 sec</option><option value="10">10 sec</option></select>
      </div>
      {f.source === "photo" && photos.length > 1 && <select value={f.media_id} onChange={(e) => setF({ ...f, media_id: e.target.value })} className="input-luxe !py-1 text-xs" data-testid="video-ai-photo">{photos.map((p, i) => <option key={p.media_id} value={p.media_id}>Photo {i + 1}</option>)}</select>}
      <input value={f.style} onChange={(e) => setF({ ...f, style: e.target.value })} maxLength={300} placeholder="Optional: 'slow push-in, fireworks bloom at the end'" className="input-luxe !py-1.5 text-xs" data-testid="video-ai-style" />
      <button onClick={make} disabled={busy || running || (f.source === "art" ? !inv.art_path : !photos.length)} className="btn-gold text-xs disabled:opacity-40" data-testid="video-ai-btn">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Clapperboard className="w-3 h-3" />} {running ? "Filming…" : "Make AI clip"}</button>
      <div className="space-y-2">
        {list.map((v) => (
          <div key={v.video_id} className="flex items-center gap-2 text-xs" data-testid={`video-item-${v.video_id}`}>
            {v.status === "done" ? <>
              <video src={`${fileUrl(v.path)}#t=0.1`} preload="metadata" controls playsInline className="w-20 rounded-lg bg-black" data-testid={`video-player-${v.video_id}`} />
              <a href={fileUrl(v.path)} download className="text-[var(--gold)] flex items-center gap-1" data-testid={`video-download-${v.video_id}`}><Download className="w-3 h-3" /> Download</a>
            </> : v.status === "running" ? <span className="flex items-center gap-1 text-[var(--muted)]"><Loader2 className="w-3 h-3 animate-spin" /> Filming your {v.duration}s clip…</span>
              : <span className="text-red-600">{v.error || "Failed"}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function InviteVideoCard({ inv, names }) {
  return (
    <div className="pt-4 border-t border-[var(--line)] space-y-4" data-testid="invite-video">
      <p className="text-[11px] uppercase tracking-[0.2em] text-[var(--gold)]">Video for Instagram & TikTok</p>
      <RecordExact inv={inv} names={names} />
      <AiClip inv={inv} />
    </div>
  );
}
